import { NodeViewWrapper, type NodeViewProps } from '@tiptap/react';
import { useEffect, useState } from 'react';

import { Button } from '../../../../primitives/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../../primitives/dialog';
import { Input } from '../../../../primitives/input';
import { ComboboxModel } from '../../../combobox/combobox-model';
import { LabelWithoutRef } from '../../../label/label-without-ref';

function validateForm(mention: string) {
  if (!mention.trim()) {
    return 'Debes seleccionar un dato del cliente';
  }
  return '';
}

export function MentionNodeView({ node, getPos, editor, extension }: NodeViewProps) {
  const items: string[] = extension.options?.items || [];
  const model = items.map((variable) => ({ value: variable, item: variable }));

  const originalMentionValue = node.attrs.mention || '';
  const originalDefaultValue = node.attrs.default || '';

  const [openDialog, setOpenDialog] = useState(false);
  const [form, setForm] = useState({
    mention: originalMentionValue,
    default: originalDefaultValue,
    error: '',
  });

  useEffect(() => {
    if (openDialog) return;

    setForm({
      mention: node.attrs.mention || '',
      default: node.attrs.default || '',
      error: '',
    });
  }, [openDialog]);

  function handleConfirm() {
    const newMentionValue = form.mention;
    const newDefaultValue = form.default.trim() || null;

    const errorMessage = validateForm(newMentionValue);
    if (errorMessage) {
      setForm((prev) => ({ ...prev, error: errorMessage }));
      return;
    }

    const nodesCurrentPosition =
      typeof getPos === 'function' ? getPos() : typeof getPos === 'number' ? getPos : null;

    if (typeof nodesCurrentPosition === 'number') {
      editor
        .chain()
        .focus()
        .setNodeSelection(nodesCurrentPosition)
        .updateAttributes('mention', {
          mention: newMentionValue,
          default: newDefaultValue,
        })
        .run();
    } else {
      editor
        .chain()
        .focus()
        .updateAttributes('mention', {
          mention: newMentionValue,
          default: newDefaultValue,
        })
        .run();
    }

    setOpenDialog(false);
  }

  return (
    <NodeViewWrapper as="span">
      <span
        className="mention cursor-pointer"
        data-mention={originalMentionValue}
        data-default={originalDefaultValue}
        onClick={() => setOpenDialog(true)}
      >
        {originalDefaultValue
          ? `{{ ${originalMentionValue} | default('${originalDefaultValue}') }}`
          : `{{ ${originalMentionValue} }}`}
      </span>

      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="h-auto max-w-lg rounded-lg md:h-auto">
          <DialogHeader className="mb-8 px-1">
            <DialogTitle className="leading-7">
              Modificar{' '}
              <span className="rounded-3xl bg-accent/50 px-2 py-[0.2rem] text-base font-normal text-accent-foreground">
                {originalMentionValue}
              </span>
            </DialogTitle>
            <DialogDescription>
              Actualiza el dato del cliente por otro del listado y/o actualiza el valor default que
              se mostrará cuando el dato no exista o no pueda obtenerse.
            </DialogDescription>
          </DialogHeader>

          <section className="grid gap-4 px-1">
            <div className="space-y-1">
              <LabelWithoutRef>Dato del cliente</LabelWithoutRef>
              <ComboboxModel
                model={model}
                value={form.mention}
                onSelect={(value) =>
                  setForm((prev) => ({
                    ...prev,
                    mention: value,
                    error: validateForm(value),
                  }))
                }
              />
            </div>

            <div className="space-y-1">
              <LabelWithoutRef>
                Valor default <span className="text-xs text-muted-foreground">opcional</span>
              </LabelWithoutRef>
              <Input
                autoFocus
                placeholder="Ej. Plus, Inversor, gbm@email.com"
                value={form.default}
                onChange={(e) => {
                  const sanitized = e.target.value.replace(/'/g, '');

                  setForm((prev) => ({
                    ...prev,
                    default: sanitized,
                    error: validateForm(prev.mention),
                  }));
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleConfirm()}
              />
              {form.error && <p className="text-sm text-destructive">{form.error}</p>}
            </div>

            <div className="mt-10 flex flex-col-reverse gap-4 sm:flex-row sm:justify-end">
              <Button
                title="Cancelar"
                className="min-w-32"
                variant="destructive"
                onClick={() => setOpenDialog(false)}
              >
                Cancelar
              </Button>
              <Button
                title="Confirmar"
                className="min-w-32"
                disabled={form.error !== ''}
                onClick={handleConfirm}
              >
                Confirmar
              </Button>
            </div>
          </section>
        </DialogContent>
      </Dialog>
    </NodeViewWrapper>
  );
}
